import React, { useState, useEffect } from 'react';
import { getStatusDisplayName, getStatusColor, getStatusIcon } from '../constants/statusTypes';
import { useMetrics } from '../contexts/MetricsContext';
import './EditLogEntryModal.css';

const EditLogEntryModal = ({ isOpen, onClose, onSave, entry, metric }) => {
  const [date, setDate] = useState('');
  const [numericValue, setNumericValue] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [notes, setNotes] = useState('');
  const [calculatedStatus, setCalculatedStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { checkDailyEntryExists } = useMetrics();

  // Initialize form with entry data
  useEffect(() => {
    if (entry && metric) {
      setDate(entry.date || '');
      setNumericValue(metric.type === 'numeric' ? entry.value?.toString() || '' : '');
      setSelectedOption(metric.type === 'list' ? entry.value || '' : '');
      setNotes(entry.notes || '');
      setCalculatedStatus(entry.status || null);
    }
  }, [entry, metric]);

  // Calculate status based on value and metric configuration
  useEffect(() => {
    if (!metric || !metric.statusConfig) return;

    if (metric.type === 'numeric' && numericValue !== '') {
      const value = parseFloat(numericValue);
      if (isNaN(value)) {
        setCalculatedStatus(null);
        return;
      }

      const { statusConfig } = metric;

      // Check operational status first
      if (statusConfig.operational.operator === 'range') {
        if (value >= statusConfig.operational.min && value <= statusConfig.operational.max) {
          setCalculatedStatus('operational');
          return;
        }
      } else if (statusConfig.operational.operator === '>=' && value >= statusConfig.operational.value) {
        setCalculatedStatus('operational');
        return;
      } else if (statusConfig.operational.operator === '>' && value > statusConfig.operational.value) {
        setCalculatedStatus('operational');
        return;
      } else if (statusConfig.operational.operator === '=' && value === statusConfig.operational.value) {
        setCalculatedStatus('operational');
        return;
      } else if (statusConfig.operational.operator === '<=' && value <= statusConfig.operational.value) {
        setCalculatedStatus('operational');
        return;
      } else if (statusConfig.operational.operator === '<' && value < statusConfig.operational.value) {
        setCalculatedStatus('operational');
        return;
      }

      // Check degraded status
      if (statusConfig.degraded.operator === 'range') {
        if (value >= statusConfig.degraded.min && value <= statusConfig.degraded.max) {
          setCalculatedStatus('degraded');
          return;
        }
      } else if (statusConfig.degraded.operator === '>=' && value >= statusConfig.degraded.value) {
        setCalculatedStatus('degraded');
        return;
      } else if (statusConfig.degraded.operator === '>' && value > statusConfig.degraded.value) {
        setCalculatedStatus('degraded');
        return;
      } else if (statusConfig.degraded.operator === '=' && value === statusConfig.degraded.value) {
        setCalculatedStatus('degraded');
        return;
      } else if (statusConfig.degraded.operator === '<=' && value <= statusConfig.degraded.value) {
        setCalculatedStatus('degraded');
        return;
      } else if (statusConfig.degraded.operator === '<' && value < statusConfig.degraded.value) {
        setCalculatedStatus('degraded');
        return;
      }
      
      // Default to outage
      setCalculatedStatus('outage');
    } else if (metric.type === 'list' && selectedOption) {
      const option = metric.statusConfig.options.find(opt => opt.name === selectedOption);
      if (option) {
        setCalculatedStatus(option.status);
      } else {
        setCalculatedStatus(null);
      }
    } else {
      setCalculatedStatus(null);
    }
  }, [metric, selectedOption, numericValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !calculatedStatus) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if an entry already exists for this date (excluding current entry)
    if (checkDailyEntryExists(metric.id, date, entry.id)) {
      setError('An entry already exists for this date. Please choose a different date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const updatedEntry = {
        date: date,
        status: calculatedStatus,
        value: metric.type === 'numeric' ? parseFloat(numericValue) : selectedOption,
        notes: notes.trim(),
        timestamp: new Date().toISOString()
      };

      await onSave(entry.id, updatedEntry);
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to update log entry');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Edit Entry</h3>
          <button 
            className="modal-close-btn"
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="date">Date *</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          {metric?.type === 'numeric' && (
            <div className="form-group">
              <label htmlFor="value">Value *</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  id="value"
                  value={numericValue}
                  onChange={(e) => setNumericValue(e.target.value)}
                  step="any"
                  required
                  disabled={loading}
                  placeholder="Enter value"
                />
                <span className="unit-display">{metric.unit}</span>
              </div>
            </div>
          )}

          {metric?.type === 'list' && (
            <div className="form-group">
              <label htmlFor="option">Option *</label>
              <select
                id="option"
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                required
                disabled={loading}
              >
                <option value="">Select an option</option>
                {metric.statusConfig?.options?.map((option, index) => (
                  <option key={index} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              placeholder="Optional notes about this entry"
              rows="3"
            />
          </div>

          {calculatedStatus && (
            <div className="status-preview">
              <span className="status-label">Status:</span>
              <span 
                className="status-value"
                style={{ color: getStatusColor(calculatedStatus) }}
              >
                {getStatusIcon(calculatedStatus)} {getStatusDisplayName(calculatedStatus)}
              </span>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading || !calculatedStatus}
            >
              {loading ? 'Updating...' : 'Update Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLogEntryModal;
